// client/src/components/quote/QuoteModal.js
import React, { useState, useEffect } from 'react';
import { useQuote } from '../../context/QuoteContext';
import axios from 'axios';
import Select from 'react-select';
import { getCookiePreferences } from '../layout/CookieConsent';
import './QuoteModal.css';

const SAVED_CONTACT_KEY = 'makah_saved_quote_contact';

const QuoteModal = () => {
  const {
    isModalOpen,
    closeModal,
    productForQuote,
    quoteItems,
    addToQuoteList,
    updateQuoteItemQuantity,
    removeFromQuoteList,
    clearQuoteList,
  } = useQuote();

  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCustomProduct, setIsCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('quote'); // 'quote' | 'details'
  const [status, setStatus] = useState({ message: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedContact, setSavedContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
  });

  // Fetch products once on mount
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/products`)
      .then((response) => {
        const formattedProducts = response.data.map((p) => ({
          value: p.id,
          label: p.name,
          product: p,
        }));
        setAllProducts(formattedProducts);
      })
      .catch((error) => console.error('Could not fetch products', error));
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setQuantity(1);
      if (productForQuote) {
        const match = allProducts.find((p) => p.value === productForQuote.id);
        setSelectedProduct(
          match || {
            value: productForQuote.id,
            label: productForQuote.name,
            product: productForQuote,
          }
        );
        setIsCustomProduct(false);
        setActiveTab(productForQuote.openDetailsFirst ? 'details' : 'quote');
      } else {
        setSelectedProduct(null);
        setIsCustomProduct(false);
        setCustomProductName('');
        setActiveTab('quote');
      }
      setStatus({ message: '', type: '' });
      setIsSubmitting(false);

      // Load saved contact info if functional cookies are allowed
      const prefs = getCookiePreferences();
      if (!prefs || prefs.functional) {
        try {
          const stored = localStorage.getItem(SAVED_CONTACT_KEY);
          if (stored) {
            setSavedContact(JSON.parse(stored));
          }
        } catch (err) {
          console.error('Could not read saved contact details', err);
        }
      }
    }
  }, [isModalOpen, productForQuote, allProducts]);

  const inspectedProduct = selectedProduct?.product || productForQuote;

  const handleAddCurrentSelectionToList = () => {
    if (isCustomProduct) {
      if (!customProductName.trim()) {
        setStatus({ message: 'Please enter a custom product name to add.', type: 'error' });
        return;
      }
      addToQuoteList(
        {
          id: `custom-${customProductName.trim().toLowerCase()}`,
          name: customProductName.trim(),
          category: 'Custom Request',
        },
        quantity
      );
      setCustomProductName('');
      setQuantity(1);
      setStatus({ message: 'Added custom item to your quote list.', type: 'info' });
    } else if (selectedProduct) {
      addToQuoteList(
        selectedProduct.product || {
          id: selectedProduct.value,
          name: selectedProduct.label,
        },
        quantity
      );
      setSelectedProduct(null);
      setQuantity(1);
      setStatus({ message: 'Added product to your quote list.', type: 'info' });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ message: 'Sending your quote inquiry...', type: 'info' });

    const formData = new FormData(e.target);
    const contactPayload = {
      name: formData.get('name'),
      company: formData.get('company'),
      email: formData.get('email'),
      phone: formData.get('phone'),
    };

    // Build combined product list if the user has items in their quote list +/or a currently selected item
    const itemsToSubmit = [...quoteItems];
    const currentProductName = isCustomProduct
      ? customProductName.trim()
      : selectedProduct?.label;

    if (currentProductName) {
      const alreadyInList = itemsToSubmit.some(
        (item) => item.name.toLowerCase() === currentProductName.toLowerCase()
      );
      if (!alreadyInList) {
        itemsToSubmit.push({
          name: currentProductName,
          quantity: Number(quantity) || 1,
          isCustom: isCustomProduct,
        });
      }
    }

    if (itemsToSubmit.length === 0) {
      setStatus({
        message: 'Please select a product, enter a custom one, or add items to your quote list.',
        type: 'error',
      });
      setIsSubmitting(false);
      return;
    }

    const totalQuantity = itemsToSubmit.reduce(
      (sum, item) => sum + (Number(item.quantity) || 1),
      0
    );

    const formattedProductNames =
      itemsToSubmit.length === 1
        ? itemsToSubmit[0].name
        : itemsToSubmit.map((item) => `${item.name} (x${item.quantity})`).join(', ');

    const quoteData = {
      productName: formattedProductNames,
      quantity: itemsToSubmit.length === 1 ? itemsToSubmit[0].quantity : totalQuantity,
      ...contactPayload,
      isCustomRequest:
        isCustomProduct || itemsToSubmit.some((item) => item.category === 'Custom Request'),
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/quotes`, quoteData);

      const prefs = getCookiePreferences();
      if (!prefs || prefs.functional) {
        try {
          localStorage.setItem(SAVED_CONTACT_KEY, JSON.stringify(contactPayload));
        } catch (err) {
          console.error('Could not store contact details', err);
        }
      }

      clearQuoteList();
      setStatus({
        message: 'Success! Your quote request has been sent. Our team will contact you shortly.',
        type: 'success',
      });
      e.target.reset();
      setTimeout(closeModal, 2600);
    } catch (error) {
      setStatus({
        message: 'An error occurred. Please check your details and try again.',
        type: 'error',
      });
      setIsSubmitting(false);
    }
  };

  const toggleCustomProduct = () => {
    setIsCustomProduct(!isCustomProduct);
    setSelectedProduct(null);
  };

  if (!isModalOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={closeModal}>
      <div className="quote-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-modal-btn" onClick={closeModal} aria-label="Close modal">
          <i className="fas fa-times"></i>
        </button>

        {/* --- Non-Scrolling Header --- */}
        <div className="modal-header">
          <h2>Request a Medical Supply Quote</h2>
          <p>
            Select single or multiple equipment items below. A Makah Medics specialist will respond
            promptly.
          </p>

          {inspectedProduct && (
            <div className="modal-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'quote'}
                className={`modal-tab-btn ${activeTab === 'quote' ? 'active' : ''}`}
                onClick={() => setActiveTab('quote')}
              >
                <i className="fas fa-file-invoice"></i>
                <span>Quote Form</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'details'}
                className={`modal-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <i className="fas fa-info-circle"></i>
                <span>Product Specifications</span>
              </button>
            </div>
          )}
        </div>

        {/* --- Scrolling Content --- */}
        <div className="modal-form-content">
          {activeTab === 'details' && inspectedProduct ? (
            <div className="product-spec-preview">
              <div className="spec-preview-media">
                <img
                  src={inspectedProduct.imageUrl || '/images/placeholder.png'}
                  alt={inspectedProduct.name}
                />
              </div>
              <div className="spec-preview-info">
                <span className="spec-category-badge">
                  {inspectedProduct.category || 'Medical Equipment'}
                </span>
                <h3>{inspectedProduct.name}</h3>
                <p className="spec-description">
                  {inspectedProduct.description ||
                    'High-grade clinical equipment supplied and supported by Makah Medics across Tanzania with full warranty, installation, and preventative maintenance support.'}
                </p>

                <ul className="spec-highlights">
                  <li>
                    <i className="fas fa-check-circle"></i> Certified Clinical Quality & Warranty
                  </li>
                  <li>
                    <i className="fas fa-tools"></i> Nationwide Biomedical Maintenance Support
                  </li>
                  <li>
                    <i className="fas fa-shipping-fast"></i> Fast Dispatch from Dar es Salaam Hub
                  </li>
                </ul>

                <div className="spec-preview-actions">
                  <button
                    type="button"
                    className="btn-submit-quote"
                    onClick={() => setActiveTab('quote')}
                  >
                    Proceed to Request Quote <i className="fas fa-arrow-right"></i>
                  </button>
                  <a
                    href={`https://wa.me/255769880769?text=${encodeURIComponent(
                      `Hello Makah Medics, I would like technical specifications and pricing for: ${inspectedProduct.name}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp-spec"
                  >
                    <i className="fab fa-whatsapp"></i> Ask via WhatsApp
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit}>
              {/* --- Multi-Item Quote Basket --- */}
              {quoteItems.length > 0 && (
                <div className="quote-cart-box">
                  <div className="quote-cart-header">
                    <span>
                      <i className="fas fa-clipboard-list"></i> Items in Your Quote List (
                      {quoteItems.length})
                    </span>
                    <button
                      type="button"
                      className="clear-cart-btn"
                      onClick={clearQuoteList}
                    >
                      Clear All
                    </button>
                  </div>
                  <ul className="quote-cart-items">
                    {quoteItems.map((item) => (
                      <li key={item.id || item.name} className="quote-cart-item">
                        <div className="quote-cart-item-info">
                          <strong>{item.name}</strong>
                          <small>{item.category}</small>
                        </div>
                        <div className="quote-cart-item-controls">
                          <label className="sr-only" htmlFor={`qty-${item.id}`}>
                            Quantity
                          </label>
                          <input
                            id={`qty-${item.id}`}
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateQuoteItemQuantity(item.id || item.name, e.target.value)
                            }
                            className="cart-qty-input"
                          />
                          <button
                            type="button"
                            className="cart-remove-btn"
                            onClick={() => removeFromQuoteList(item.id || item.name)}
                            aria-label={`Remove ${item.name}`}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* --- Product Selector + Quantity Row --- */}
              <div className="product-selector-row">
                <div className="form-group product-select-col">
                  <label htmlFor="product-search">
                    {quoteItems.length > 0 ? 'Add Another Product (Optional)' : 'Product of Interest'}
                  </label>
                  {isCustomProduct ? (
                    <input
                      type="text"
                      id="custom-product"
                      className="form-input"
                      placeholder="e.g., 'GE Vivid E95 Ultrasound Machine'"
                      value={customProductName}
                      onChange={(e) => setCustomProductName(e.target.value)}
                      required={quoteItems.length === 0}
                    />
                  ) : (
                    <Select
                      inputId="product-search"
                      options={allProducts}
                      value={selectedProduct}
                      onChange={setSelectedProduct}
                      placeholder="Type to search for a product..."
                      noOptionsMessage={() => 'No matching products found'}
                      isClearable
                      classNamePrefix="react-select"
                    />
                  )}
                  <button
                    type="button"
                    className="toggle-custom-btn"
                    onClick={toggleCustomProduct}
                  >
                    {isCustomProduct
                      ? 'Or Select from Our Catalog'
                      : "Can't find your product? Enter a custom item"}
                  </button>
                </div>

                <div className="form-group quantity-group">
                  <label htmlFor="quantity">Qty</label>
                  <input
                    type="number"
                    className="form-input"
                    id="quantity"
                    name="quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                    min="1"
                  />
                </div>

                <div className="add-to-list-col">
                  <button
                    type="button"
                    className="btn-add-to-list"
                    onClick={handleAddCurrentSelectionToList}
                    disabled={!selectedProduct && !customProductName.trim()}
                    title="Add item to multi-product quote list"
                  >
                    <i className="fas fa-plus"></i> Add to List
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    id="name"
                    name="name"
                    defaultValue={savedContact.name}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Company / Hospital Name</label>
                  <input
                    type="text"
                    className="form-input"
                    id="company"
                    name="company"
                    defaultValue={savedContact.company}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    className="form-input"
                    id="email"
                    name="email"
                    defaultValue={savedContact.email}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    className="form-input"
                    id="phone"
                    name="phone"
                    defaultValue={savedContact.phone}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit-quote" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i> Sending Request...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i> Submit Quote Request
                  </>
                )}
              </button>
            </form>
          )}

          {status.message && (
            <div className={`status-message ${status.type}`}>{status.message}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
